import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  History, Search, ArrowUpRight, ArrowDownRight, Wallet, Filter,
  Calendar, Euro, Download, RefreshCw, CreditCard, Gift, Clock
} from 'lucide-react';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const TRANSACTION_TYPES = {
  credit: { label: 'Crédit', icon: ArrowUpRight, color: 'text-green-600 bg-green-100' },
  debit: { label: 'Débit', icon: ArrowDownRight, color: 'text-red-600 bg-red-100' },
  bonus: { label: 'Bonus', icon: Gift, color: 'text-purple-600 bg-purple-100' },
  refund: { label: 'Remboursement', icon: RefreshCw, color: 'text-blue-600 bg-blue-100' }
};

const WalletHistoryPage = () => {
  const { token, user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');

  // Summary stats
  const [summary, setSummary] = useState({
    totalCredits: 0,
    totalDebits: 0,
    currentBalance: 0,
    pendingBalance: 0
  });

  // Fetch transactions
  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      // Try to fetch wallet data
      const response = await api.get('/wallet/');
      const walletData = response.data;
      
      // Get transactions from wallet or use mock data
      const txns = walletData?.transactions || [];
      
      if (txns.length === 0) {
        // Mock data for demonstration
        const mockTransactions = [
          {
            id: '1',
            type: 'credit',
            amount: 500,
            description: 'Rechargement wallet',
            method: 'Carte bancaire',
            reference: 'WLT-2024-001',
            created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
            status: 'completed'
          },
          {
            id: '2',
            type: 'debit',
            amount: -150,
            description: 'Paiement projet - Site Web',
            method: 'Wallet',
            reference: 'PRJ-2024-042',
            created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
            status: 'completed'
          },
          {
            id: '3',
            type: 'bonus',
            amount: 50,
            description: 'Bonus de bienvenue',
            method: 'Automatique',
            reference: 'BON-2024-001',
            created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
            status: 'completed'
          },
          {
            id: '4',
            type: 'debit',
            amount: -200,
            description: 'Paiement projet - Vidéo promo',
            method: 'Wallet',
            reference: 'PRJ-2024-038',
            created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
            status: 'completed'
          },
          {
            id: '5',
            type: 'credit',
            amount: 1000,
            description: 'Rechargement wallet',
            method: 'Virement bancaire',
            reference: 'WLT-2024-002',
            created_at: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
            status: 'completed'
          },
          {
            id: '6',
            type: 'refund',
            amount: 75,
            description: 'Remboursement projet annulé',
            method: 'Automatique',
            reference: 'REF-2024-001',
            created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
            status: 'completed'
          }
        ];
        setTransactions(mockTransactions);
        
        // Calculate summary from mock data
        const credits = mockTransactions.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0);
        const debits = mockTransactions.filter(t => t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount), 0);
        setSummary({
          totalCredits: credits,
          totalDebits: debits,
          currentBalance: walletData?.balance || credits - debits,
          pendingBalance: walletData?.pending_balance || 0
        });
      } else {
        setTransactions(txns);
        const credits = txns.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0);
        const debits = txns.filter(t => t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount), 0);
        setSummary({
          totalCredits: credits,
          totalDebits: debits,
          currentBalance: walletData?.balance || 0,
          pendingBalance: walletData?.pending_balance || 0
        });
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const filteredTransactions = transactions.filter(txn => {
    const matchesSearch = txn.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         txn.reference?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || txn.type === filterType;
    
    // Period filter
    let matchesPeriod = true;
    if (filterPeriod !== 'all') {
      const txnDate = new Date(txn.created_at);
      const now = new Date();
      if (filterPeriod === 'week') {
        const weekAgo = new Date(now.setDate(now.getDate() - 7));
        matchesPeriod = txnDate >= weekAgo;
      } else if (filterPeriod === 'month') {
        const monthAgo = new Date(now.setMonth(now.getMonth() - 1));
        matchesPeriod = txnDate >= monthAgo;
      } else if (filterPeriod === 'quarter') {
        const quarterAgo = new Date(now.setMonth(now.getMonth() - 3));
        matchesPeriod = txnDate >= quarterAgo;
      }
    }
    
    return matchesSearch && matchesType && matchesPeriod;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Math.abs(amount));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      
      <div className={cn(
        "flex-1 flex flex-col transition-all duration-300",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        <Header setMobileOpen={setMobileOpen} />
        
        <main className="flex-1 p-4 md:p-6 pt-32 overflow-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold">Historique des transactions</h1>
              <p className="text-muted-foreground">Suivez tous les mouvements de votre wallet</p>
            </div>
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Exporter
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Wallet className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Solde actuel</p>
                    <p className="text-lg font-bold">{formatCurrency(summary.currentBalance)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <ArrowUpRight className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total crédits</p>
                    <p className="text-lg font-bold text-green-600">+{formatCurrency(summary.totalCredits)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <ArrowDownRight className="h-4 w-4 text-red-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total débits</p>
                    <p className="text-lg font-bold text-red-600">-{formatCurrency(summary.totalDebits)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Clock className="h-4 w-4 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">En attente</p>
                    <p className="text-lg font-bold text-orange-600">{formatCurrency(summary.pendingBalance)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher par description ou référence..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="px-3 py-2 border rounded-lg text-sm"
                  >
                    <option value="all">Tous les types</option>
                    <option value="credit">Crédits</option>
                    <option value="debit">Débits</option>
                    <option value="bonus">Bonus</option>
                    <option value="refund">Remboursements</option>
                  </select>
                  <select
                    value={filterPeriod}
                    onChange={(e) => setFilterPeriod(e.target.value)}
                    className="px-3 py-2 border rounded-lg text-sm"
                  >
                    <option value="all">Toute la période</option>
                    <option value="week">Cette semaine</option>
                    <option value="month">Ce mois</option>
                    <option value="quarter">Ce trimestre</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Transactions List */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : filteredTransactions.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <History className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Aucune transaction</h3>
                <p className="text-muted-foreground">Aucune transaction ne correspond à vos critères.</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="divide-y">
                  {filteredTransactions.map((txn) => {
                    const type = TRANSACTION_TYPES[txn.type] || TRANSACTION_TYPES.debit;
                    const TypeIcon = type.icon;
                    const isPositive = txn.amount > 0;
                    
                    return (
                      <div key={txn.id} className="p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className={cn("p-2 rounded-lg", type.color.split(' ')[1])}>
                            <TypeIcon className={cn("h-4 w-4", type.color.split(' ')[0])} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="font-medium">{txn.description}</p>
                                <p className="text-sm text-muted-foreground">
                                  {txn.method} • {txn.reference}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className={cn(
                                  "font-bold",
                                  isPositive ? "text-green-600" : "text-red-600"
                                )}>
                                  {isPositive ? '+' : '-'}{formatCurrency(txn.amount)}
                                </p>
                                <p className="text-xs text-muted-foreground">{formatDate(txn.created_at)}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </main>
      </div>
    </div>
  );
};

export default WalletHistoryPage;
